import React, { Fragment, useState, useEffect } from "react";

import { bool, func, object, number, any } from "prop-types";
import moment from "moment";

import { Link, Redirect } from "react-router-dom";
import TimesheetsContainer from "../../containers/Timesheets";
import { TimesheetTable } from "../TimesheetTable/index";

import { isInEditableState, getDateWithOffset } from "../../helpers/timesheets";
import * as style from "./styles.module.scss";
import history from "../../history";
import { PulseLoader } from "../../common";
import LabeledValue from "../LabeledValue";
import { dayNames as days } from "../../helpers";
import TimesheetFeedback from "../TimesheetFeedback";
import { checkIfFeedbackCached } from "../TimesheetFeedback/helpers";

const displayDate = (date) => moment(date).format("DD/MM/YY");

const UpdateStatus = Object.freeze({
  Success: 0,
  Error: 1,
  Updating: 2,
  Untouched: 3,
});

const setInitialColumnTotal = (ts) => {
  return ts.columns.map((day) => {
    const dayTotal = day.StandardPayQty.reduce((sum, hr) => sum + hr, 0);
    return dayTotal;
  });
};

const calculateTimeDifference = (startTime, endTime, date) => {
  if (!startTime || !endTime) {
    return 0;
  }
  const start = new Date(`${date}T${startTime}:00`);
  const end = new Date(`${date}T${endTime}:00`);
  const timeDifferenceMs = end - start;
  const timeDifferenceHours = timeDifferenceMs / (1000 * 60 * 60);
  return timeDifferenceHours;
};

const setInitialTimesTotal = (ts) => {
  return ts.timeFrames.map((time) =>
    calculateTimeDifference(time.startTime, time.endTime, time.date)
  );
};

const calcTimesheetTotalHours = (columnTotals) =>
  columnTotals.reduce((sum, dailyTotal) => sum + dailyTotal, 0.0);

const calcTimesheetTimeFramesTotalHours = (timeTotals) =>
  timeTotals.reduce((sum, dailyTotal) => sum + dailyTotal, 0.0);

const useTimesheetState = (loadedTimesheet) => {
  const [timesheetState, setTimesheet] = useState(loadedTimesheet);
  const [columnTotals, setColumnTotals] = useState(
    setInitialColumnTotal(loadedTimesheet)
  );
  const [timeTotals, setTimeTotals] = useState(
    setInitialTimesTotal(loadedTimesheet)
  );
  const [columnErrors, setColumnErrors] = useState([
    { over24hrs: false },
    { over24hrs: false },
    { over24hrs: false },
    { over24hrs: false },
    { over24hrs: false },
    { over24hrs: false },
    { over24hrs: false },
  ]);

  const updateHour = (rateIdx, dayIdx, hours) => {
    const validatedHours = Number.isNaN(hours) ? 0.0 : hours;
    const dayColumn = timesheetState.columns[dayIdx];
    const updatedStandardPay = [...dayColumn.StandardPayQty];
    updatedStandardPay.splice(rateIdx, 1, validatedHours);

    const columns = [
      ...timesheetState.columns.slice(0, dayIdx),
      { ...dayColumn, StandardPayQty: updatedStandardPay },
      ...timesheetState.columns.slice(dayIdx + 1),
    ];

    const updatedColumnTotals = columns.map((day) => {
      return day.StandardPayQty.reduce((_acc, _hours) => _acc + _hours);
    });

    setColumnTotals(updatedColumnTotals);

    const errs = columns.map((day) => {
      const dayTotal = day.StandardPayQty.reduce((sum, hr) => sum + hr, 0);
      return {
        over24hrs: dayTotal > 24.0,
      };
    });
    setColumnErrors(errs);

    const updatedState = {
      ...timesheetState,
      columns,
    };

    setTimesheet(updatedState);
  };

  const decimalToTime = (decimal) => {
    const hours = Math.floor(decimal);
    const minutes = Math.round((decimal - hours) * 60);
    const formattedHours = hours.toString().padStart(2, "0");
    const formattedMinutes = minutes.toString().padStart(2, "0");

    return `${formattedHours}:${formattedMinutes}`;
  };

  const updateStartAndEndTime = (frames) => {
    const updatedTimeFrameTotals = frames.map((time) => {
      return calculateTimeDifference(time.startTime, time.endTime, time.date);
    });

    setTimeTotals(updatedTimeFrameTotals);

    const errs = frames.map((time) => {
      const dayTotal = calculateTimeDifference(
        time.startTime,
        time.endTime,
        time.date
      );
      return {
        over24hrs: dayTotal > 24.0,
      };
    });
    setColumnErrors(errs);

    const updatedState = {
      ...timesheetState,
      timeFrames: frames,
    };

    setTimesheet(updatedState);
  };

  const updateStartTime = (rateIdx, dayIdx, hours) => {
    const validatedHours = Number.isNaN(hours) ? 0.0 : hours;
    const updatedEndTime = decimalToTime(validatedHours);
    const dayTimeFrame = timesheetState.timeFrames[dayIdx];
    const updatedTimeFrame = { ...dayTimeFrame, startTime: updatedEndTime };

    const frames = timesheetState.timeFrames.map((timeFrame, index) => {
      if (index === dayIdx) return updatedTimeFrame;

      return timeFrame;
    });

    return updateStartAndEndTime(frames);
  };

  const updateEndTime = (rateIdx, dayIdx, hours) => {
    const validatedHours = Number.isNaN(hours) ? 0.0 : hours;
    const updatedEndTime = decimalToTime(validatedHours);
    const dayTimeFrame = timesheetState.timeFrames[dayIdx];
    const updatedTimeFrame = { ...dayTimeFrame, endTime: updatedEndTime };

    const frames = timesheetState.timeFrames.map((timeFrame, index) => {
      if (index === dayIdx) return updatedTimeFrame;

      return timeFrame;
    });

    return updateStartAndEndTime(frames);
  };

  return [
    timesheetState,
    updateHour,
    updateStartTime,
    updateEndTime,
    columnErrors,
    columnTotals,
    timeTotals,
  ];
};

const TimesheetUpdateInProgress = ({ isSaving = false }) => {
  return (
    <div className={style.timesheet__notification}>
      <PulseLoader />
      <h2 className={style.timesheet__notification_title}>
        {isSaving ? "Saving" : "Submitting"} your timesheet
      </h2>
      <b>This won’t take too long</b>
    </div>
  );
};

TimesheetUpdateInProgress.propTypes = {
  isSaving: bool,
};

const TimesheetUpdateSuccess = ({
  onClick,
  isSaving = false,
  timesheet = {},
}) => {
  const showFeedback = !isSaving && !checkIfFeedbackCached();
  return (
    <div
      className={`${style.timesheet__notification} ${
        showFeedback && "mt-5 mt-md-0"
      }`}
    >
      <img src="/images/icons/timesheet-approve.svg" />
      <h2 className={style.timesheet__notification_title}>
        <span className="d-none d-md-inline-block">Timesheet</span>{" "}
        <span className={style.timesheet__notification_titleMobile}>
          {isSaving ? "saved" : "submitted"}
        </span>{" "}
        successfully
      </h2>
      <b className={style.timesheet__notification_subtitle}>
        {isSaving
          ? "Your timesheet has been saved."
          : "Your timesheet has now been submitted to your line manager for approval."}
      </b>
      {isSaving && (
        <b className={"text-center"}>
          Please ensure any further updates are submitted by 10am Monday.
        </b>
      )}
      {showFeedback && (
        <Fragment>
          <TimesheetFeedback
            timesheetNumber={timesheet.timesheetNumber}
            jobTitle={timesheet.jobTitle}
            orgName={timesheet.organisationTitle}
          />
        </Fragment>
      )}
      <div
        className={`${
          showFeedback && style.timesheet__notification_overviewButton_wrapper
        }`}
      >
        <button
          className={`btn btn-primary ${style.timesheet__notification_overviewButton} mb-3`}
          onClick={onClick}
        >
          Back to overview
        </button>
      </div>
    </div>
  );
};

TimesheetUpdateSuccess.propTypes = {
  onClick: func,
  isSaving: bool,
  timesheet: any,
};

const TimesheetUpdateError = ({ onClick, isSaving = false }) => {
  return (
    <div className={style.timesheet__notification}>
      <img src="/images/icons/timesheet-error.svg" />
      <h2 className={style.timesheet__notification_title}>
        {isSaving ? "Saving" : "Submission"} failed
      </h2>
      <b className={style.timesheet__notification_subtitle}>
        There has been an error in{" "}
        {isSaving
          ? "saving your timesheet."
          : "submitting your timesheet for approval."}
        {"\n"}
        Please click on the button below to retry.
      </b>
      <button
        className={`btn btn-primary ${style.timesheet__notification_overviewButton}`}
        onClick={onClick}
      >
        Try again
      </button>
    </div>
  );
};

TimesheetUpdateError.propTypes = {
  onClick: func,
  isSaving: bool,
};
const Timesheet = ({
  timesheet,
  updateTimesheet,
  isEditing = false,
  errors,
  clearErrorsWithSource,
  nonAttendanceCodes,
}) => {
  const [
    timesheetState,
    updateHour,
    updateStartTime,
    updateEndTime,
    columnErrors,
    columnTotals,
    timeTotals,
  ] = useTimesheetState(timesheet);

  const timesheetErrors = errors.filter(
    (error) => error.source === "timesheets"
  );

  const [updateStatus, setUpdateStatus] = useState(UpdateStatus.Untouched);
  const [updateState, setUpdateState] = useState(false);
  const [isFormTouched, setFormTouched] = useState(false);
  const [isSaving, setSaving] = useState(false);

  useEffect(() => {
    if (timesheet.hasUpdated && updateState) {
      if (timesheetErrors.length === 0) {
        setUpdateStatus(UpdateStatus.Success);
      } else {
        setUpdateStatus(UpdateStatus.Error);
      }
    }
    if (timesheet.isUpdating) setUpdateStatus(UpdateStatus.Updating);
  }, [timesheet]);

  const handleUpdateTimesheet = (shouldSendNotification, saving = false) => {
    if (saving) {
      setSaving(true);
    } else {
      setSaving(false);
    }
    setUpdateState(true);
    updateTimesheet(timesheetState, shouldSendNotification);
    clearErrorsWithSource("timesheets");
  };

  const handleNotificationClick = () => {
    setUpdateState(false);
    setUpdateStatus(UpdateStatus.Untouched);
    history.push("/profile/edit/Timesheets/");
  };
  const startDate = new Date(timesheet.startDate);

  const bookedDateStrs = timesheet.bookedDates.map((bd) =>
    moment(bd).format("DD/MM/YYYY")
  );

  const bookedDays = days.map(
    (_day, i) =>
      bookedDateStrs.indexOf(
        moment(getDateWithOffset(startDate, i)).format("DD/MM/YYYY")
      ) !== -1
  );

  const nonAttendanceDays = days.map(
    (_day, i) => timesheetState.columns[i].NonAttendanceCode
  );

  const isDisabledWithError = columnErrors
    .map((err, i) => Object.values(err).includes(true) && bookedDays[i])
    .includes(true);

  const isDisabledNoTime = calcTimesheetTotalHours(columnTotals) < 0.02;
  const isDisabledNoTimeTotalHours =
    calcTimesheetTimeFramesTotalHours(timeTotals) < 0.02;
  const { submitted = false } = timesheet;
  const isDisabledCauseStatus = submitted;

  const isDisabled =
    isDisabledWithError || isDisabledNoTime || isDisabledCauseStatus;
  const isDisabledTimeFrames =
    isDisabledWithError || isDisabledNoTimeTotalHours || isDisabledCauseStatus;

  switch (updateStatus) {
    case UpdateStatus.Success:
      return (
        <TimesheetUpdateSuccess
          onClick={handleNotificationClick}
          isSaving={isSaving}
          timesheet={timesheet}
        />
      );
    case UpdateStatus.Error:
      return (
        <TimesheetUpdateError
          onClick={
            isSaving
              ? () => handleUpdateTimesheet(false, true)
              : () => handleUpdateTimesheet(true, false)
          }
          isSaving={isSaving}
        />
      );
    case UpdateStatus.Updating:
      return <TimesheetUpdateInProgress isSaving={isSaving} />;
    case UpdateStatus.Untouched:
      return (
        <Fragment>
          <div className={style.timesheet__actions}>
            <div className="row">
              <div
                style={{ paddingTop: "1rem" }}
                className={`container d-flex justify-content-between ${style.timesheet__edit}`}
              >
                {!isEditing && (
                  <Fragment>
                    {!isEditing && isInEditableState(timesheet) && (
                      <Redirect
                        to={`/profile/edit/Timesheets/${timesheet.timesheetRef}/edit`}
                      ></Redirect>
                    )}
                    <Link
                      to={`/profile/edit/Timesheets/${timesheet.timesheetRef}/edit`}
                    >
                      {isInEditableState(timesheet) && (
                        <button
                          className={`btn btn-md ${style.btnEdit} btn-outline d-lg-none`}
                        >
                          Edit
                        </button>
                      )}
                    </Link>
                  </Fragment>
                )}
              </div>
            </div>
          </div>
          <div className={`${style.timesheet} container`}>
            <div className="d-flex justify-content-between">
              <div
                className="container"
                style={{ paddingTop: "20px", paddingBottom: "20px" }}
              >
                <h2>
                  Week Ending: {displayDate(timesheet.endDate)}
                  {!isEditing && isInEditableState(timesheet) && (
                    <Link
                      to={`/profile/edit/Timesheets/${timesheet.timesheetRef}/edit`}
                    >
                      <button
                        className={`btn btn-md ${style.btnEdit} btn-outline d-none d-lg-inline-block`}
                      >
                        Edit timesheet
                      </button>
                    </Link>
                  )}
                </h2>
                <div className="d-block d-lg-none">
                  <div>
                    <span className={style.label}>Job Title:</span>
                    {timesheet.jobTitle}
                  </div>
                  <div>
                    <span className={style.label}>Company:</span>{" "}
                    {timesheet.organisationTitle}
                  </div>
                </div>
                <div className="d-none d-lg-inline-block">
                  <p>
                    <LabeledValue header="Job Title">
                      {timesheet.jobTitle}
                    </LabeledValue>
                  </p>
                  <p>
                    <LabeledValue header="Company">
                      {timesheet.organisationTitle}
                    </LabeledValue>
                  </p>
                </div>
              </div>
              <div className="d-none d-lg-inline-block">
                <div className={style.timesheet__infoBox}>
                  <p>
                    If your timesheet is incomplete and you do not wish for it
                    to be submitted for approval yet, make sure you click the
                    ‘Save changes’ button before you close the timesheet.
                  </p>
                  <p>
                    If your timesheet is complete and ready to be sent for
                    approval then please select the ‘Save & submit timesheet’
                    button.
                  </p>
                  <p>
                    If you need to enter time for days which have not been
                    booked, please contact your consultant.
                  </p>
                  <p>
                    Please ensure that if you do not get paid for breaks then
                    you remove your break time from the hours and minutes you
                    enter into the timesheet below.
                  </p>
                </div>
              </div>
            </div>
            <div
              className={`d-block d-lg-none my-1 ${style.timesheet__infoBox_mobile}`}
            >
              <p>
                If your timesheet is incomplete and you do not wish for it to be
                submitted for approval yet, make sure you click the ‘Save
                changes’ button before you close the timesheet.
              </p>
              <p>
                If your timesheet is complete and ready to be sent for approval
                then please select the ‘Save & submit’ button.
              </p>
              <p>
                If you need to enter time for days which have not been booked,
                please contact your consultant.
              </p>
              <p>
                Please ensure that if you do not get paid for breaks then you
                remove your break time from the hours and minutes you enter into
                the timesheet below.
              </p>
            </div>
            <TimesheetTable
              timesheet={timesheetState}
              updateHour={updateHour}
              updateStartTime={updateStartTime}
              updateEndTime={updateEndTime}
              columnErrors={columnErrors}
              columnTotals={columnTotals}
              timeTotals={timeTotals}
              isEditing={isEditing && isInEditableState(timesheet)}
              updateTimesheet={updateTimesheet}
              bookedDateStrs={bookedDateStrs}
              bookedDays={bookedDays}
              nonAttendanceDays={nonAttendanceDays}
              startDate={startDate}
              setFormTouched={setFormTouched}
              nonAttendanceCodes={nonAttendanceCodes}
            />
            <div className="p-2 d-flex flex-row-reverse">
              {!isEditing && (
                <button
                  onClick={() => history.goBack()}
                  className={`btn btn-md bg-white ${style.timesheet__back} font-weight-bold d-none d-lg-inline-block mr-1`}
                >
                  Back
                </button>
              )}
              {isEditing && (
                <Fragment>
                  <button
                    disabled={
                      timesheetState.timesheetFillOption === "timeFrame"
                        ? isDisabledTimeFrames || !isFormTouched
                        : isDisabled || !isFormTouched
                    }
                    onClick={() => handleUpdateTimesheet(true, false)}
                    className="btn btn-md btn-primary font-weight-bold d-none d-lg-inline-block"
                  >
                    Save & submit timesheet
                  </button>
                  <button
                    disabled={
                      timesheetState.timesheetFillOption === "timeFrame"
                        ? isDisabledTimeFrames || !isFormTouched
                        : isDisabled || !isFormTouched
                    }
                    onClick={() => handleUpdateTimesheet(false, true)}
                    className={`btn btn-md bg-white ${style.timesheet__saveChanges} font-weight-bold d-none d-lg-inline-block mr-1`}
                  >
                    Save changes
                  </button>
                  <button
                    onClick={() => history.goBack()}
                    className={`btn btn-md bg-white ${style.timesheet__back} font-weight-bold d-none d-lg-inline-block mr-1`}
                  >
                    Back
                  </button>
                </Fragment>
              )}
              {!isEditing && (
                <div
                  className={`d-flex flex-column d-lg-none ${style.timesheet__saveButton}`}
                >
                  <button
                    onClick={() => history.goBack()}
                    className={`btn btn-md bg-white ${style.timesheet__back} font-weight-bold`}
                  >
                    Back
                  </button>
                </div>
              )}
              {isEditing && (
                <div
                  className={`d-flex flex-column d-lg-none ${style.timesheet__saveButton}`}
                >
                  <button
                    className="btn btn-md btn-primary"
                    disabled={
                      timesheetState.timesheetFillOption === "timeFrame"
                        ? isDisabledTimeFrames
                        : isDisabled
                    }
                    onClick={() => handleUpdateTimesheet(true, false)}
                  >
                    Save & submit
                  </button>
                  <button
                    disabled={
                      timesheetState.timesheetFillOption === "timeFrame"
                        ? isDisabledTimeFrames
                        : isDisabled
                    }
                    onClick={() => handleUpdateTimesheet(false, true)}
                    className={`btn btn-md bg-white my-1 ${style.timesheet__saveChanges} font-weight-bold`}
                  >
                    Save changes
                  </button>
                  <button
                    onClick={() => history.goBack()}
                    className={`btn btn-md bg-white ${style.timesheet__back} font-weight-bold`}
                  >
                    Back
                  </button>
                </div>
              )}
            </div>
          </div>
        </Fragment>
      );
    default:
      return <Fragment></Fragment>;
  }
};

Timesheet.propTypes = {
  timesheet: object.isRequired,
  isEditing: bool.isRequired,
  updateTimesheet: func.isRequired,
  errors: any,
  clearErrorsWithSource: func,
  nonAttendanceCodes: object,
};

const TimesheetLoader = ({ timesheetRef, isEditing = false }) => (
  <TimesheetsContainer isSingleTimesheet timesheetRef={timesheetRef}>
    {({
      updateTimesheet,
      timesheet,
      errors,
      clearErrorsWithSource,
      requestTimesheets,
      nonAttendanceCodes,
    }) => {
      return (
        <Timesheet
          timesheet={timesheet}
          isEditing={isEditing}
          updateTimesheet={updateTimesheet}
          errors={errors}
          clearErrorsWithSource={clearErrorsWithSource}
          requestTimesheets={requestTimesheets}
          nonAttendanceCodes={nonAttendanceCodes}
        />
      );
    }}
  </TimesheetsContainer>
);

TimesheetLoader.propTypes = {
  isEditing: bool,
  timesheetRef: number.isRequired,
};

export default TimesheetLoader;
